import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * Component to display a dropdown menu with bulk edit actions.
 *  - Actions are passed in as an array of BulkEditAction objects
 *  - The select all checkbox is displayed by default, but can be hidden by setting `showSelectAll` to false
 *  - When an action is clicked, the `actionClicked` event is emitted with the action name
 *  - When the select all checkbox is clicked, the `selectAllClicked` event is emitted with the checkbox value
 */
@Component({
  selector: 'sp-bulk-edit-menu',
  templateUrl: './bulk-edit-menu.component.html',
  styleUrls: ['./bulk-edit-menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkEditMenuComponent {
  /**
   * Actions to display in the dropdown menu
   */
  @Input() actions: BulkEditAction[] = [];

  /**
   * Whether to show the select all checkbox
   *
   * @default true
   */
  @Input() showSelectAll = true;

  /**
   * Selected items count.
   * To be used to display the number of selected items along with the checkbox label.
   */
  @Input() selectedItemsCount = 0;

  /**
   * Total items count.
   */
  @Input() totalItemsCount = 0;

  /**
   * Event emitted when an action is clicked
   */
  @Output() actionClicked = new EventEmitter<string>();

  /**
   * Event emitted when the select all checkbox is clicked
   */
  @Output() selectAllClicked = new EventEmitter<boolean>();

  /**
   * Whether the select all checkbox is checked
   * @default false
   * @internal
   */
  selectAllChecked = false;
}

/**
 * Bulk edit action model
 */
export interface BulkEditAction {
  /**
   * Action name to emit when the action is clicked.
   *
   * @example 'edit'
   * @example 'delete'
   *
   * This will be used to emit the action name when the action is clicked
   */
  name: string;

  /**
   * Action label to display in the dropdown menu.
   *
   * @example 'Edit'
   * @example 'Delete'
   */
  label: string;

  /**
   * Action icon to display in the dropdown menu (material icon name).
   *
   * @example 'edit'
   * @example 'delete'
   */
  icon: string;
}
